import { useState, useEffect } from 'react';
import RetailerLayout from './RetailerLayout';
import SandboxBanner from './SandboxBanner';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { rshd, orders as ordersApi } from '../../utils/api';
import { useNavigate } from 'react-router-dom';
import { Package, ShoppingBag, DollarSign, Plus, ScanBarcode, BarChart3, ChevronDown, ChevronUp } from 'lucide-react';
import QuickScanHistory from './dashboard/QuickScanHistory';
import FrequentlyScanned from './dashboard/FrequentlyScanned';
import ScanAnalyticsDashboard from './dashboard/ScanAnalyticsDashboard';
import WeeklyDigestWidget from './dashboard/WeeklyDigestWidget';
import PushOpenRateWidget from './dashboard/PushOpenRateWidget';
import NotificationFunnelWidget from './dashboard/NotificationFunnelWidget';
import PushOptInNudge from './dashboard/PushOptInNudge';
import GoLiveChecklistWidget from './GoLiveChecklistWidget';
import PauseOrdersToggle from './PauseOrdersToggle';
import RetailerWalletCard from './RetailerWalletCard';
import { useLanguage } from '../../contexts/LanguageContext';
import { HelpTooltip } from '../shared/HelpTooltip';

export default function RetailerDashboard({ user, onLogout }) {
  const [stats, setStats] = useState({ items: 0, orders: 0, revenue: 0 });
  const [loading, setLoading] = useState(true);
  const [showAnalytics, setShowAnalytics] = useState(false);
  const navigate = useNavigate();
  const { t, isSpanish } = useLanguage();

  useEffect(() => {
    loadStats();
  }, []);

  const loadStats = async () => {
    try {
      const [itemsRes, ordersRes] = await Promise.all([
        rshd.myItems(),
        ordersApi.list(),
      ]);
      const items = itemsRes.data?.items || itemsRes.data || [];
      const ordersList = ordersRes.data || [];
      const revenue = ordersList.reduce((sum, order) => sum + (order.total || 0), 0);
      setStats({
        items: Array.isArray(items) ? items.length : 0,
        orders: ordersList.length,
        revenue,
      });
    } catch (error) {
      console.error('Failed to load stats:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <RetailerLayout user={user} onLogout={onLogout}>
      <div className="space-y-6">
        {/* Sandbox Banner - shows status and checklist for non-live stores */}
        <SandboxBanner user={user} onStatusChange={loadStats} />

        {/* Main Grid: Welcome + Quick Actions | Quick Scan History */}
        <div className="grid lg:grid-cols-3 gap-6">
          {/* Left Column - Welcome & Stats */}
          <div className="lg:col-span-2 space-y-6">
            {/* Welcome Banner */}
            <div className="bg-gradient-to-r from-blue-500 to-indigo-600 rounded-2xl p-8 text-white shadow-lg">
              <div className="flex items-center gap-2 mb-2">
                <h2 className="text-3xl font-bold" style={{ fontFamily: 'Playfair Display, serif' }}>
                  {isSpanish ? 'Bienvenido' : 'Welcome'}, {user.name}!
                </h2>
                <HelpTooltip portal="retailer" category="dashboard" tooltipKey="main" size="md" className="text-white/70 hover:text-white" />
              </div>
              <p className="text-blue-50 text-lg">
                {isSpanish 
                  ? 'Publica ofertas sizzling hot para iniciar ventas. Conectamos y notificamos a consumidores locales interesados.'
                  : 'Post sizzling hot deals to initiate sales. We match and notify interested local consumers.'}
              </p>
              <div className="flex gap-3 mt-6">
                <Button
                  data-testid="scan-product-btn"
                  onClick={() => navigate('/retailer/scan')}
                  className="bg-white text-blue-600 hover:bg-blue-50"
                >
                  <ScanBarcode className="w-4 h-4 mr-2" />
                  {isSpanish ? 'Escanear Producto' : 'Scan Product'}
                </Button>
                <Button
                  data-testid="post-deal-btn"
                  onClick={() => navigate('/retailer/post-item')}
                  variant="outline"
                  className="border-white text-white hover:bg-white/10"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  {isSpanish ? 'Entrada Manual' : 'Manual Entry'}
                </Button>
              </div>
            </div>

            {/* Stats Cards */}
            <div className="grid md:grid-cols-2 gap-4">
              <GoLiveChecklistWidget />
              <PauseOrdersToggle />
            </div>

            {/* Retailer Wallet — live MTD net from Refund & Settlement System */}
            <RetailerWalletCard />

            <div className="grid md:grid-cols-3 gap-4">
              <Card>
                <CardContent className="pt-6">
                  <div className="flex items-center space-x-4">
                    <div className="bg-blue-100 p-3 rounded-lg">
                      <Package className="w-6 h-6 text-blue-600" />
                    </div>
                    <div>
                      <p className="text-sm text-gray-600">
                        {isSpanish ? 'Ofertas Activas' : 'Active Deals'}
                      </p>
                      <p className="text-2xl font-bold text-gray-900" data-testid="active-items-count">
                        {stats.items}
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardContent className="pt-6">
                  <div className="flex items-center space-x-4">
                    <div className="bg-green-100 p-3 rounded-lg">
                      <ShoppingBag className="w-6 h-6 text-green-600" />
                    </div>
                    <div>
                      <p className="text-sm text-gray-600">
                        {isSpanish ? 'Total Pedidos' : 'Total Orders'}
                      </p>
                      <p className="text-2xl font-bold text-gray-900" data-testid="total-orders-count">
                        {stats.orders}
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardContent className="pt-6">
                  <div className="flex items-center space-x-4">
                    <div className="bg-purple-100 p-3 rounded-lg">
                      <DollarSign className="w-6 h-6 text-purple-600" />
                    </div>
                    <div>
                      <p className="text-sm text-gray-600">
                        {isSpanish ? 'Ingresos' : 'Revenue'}
                      </p>
                      <p className="text-2xl font-bold text-gray-900" data-testid="total-revenue">
                        ${stats.revenue.toFixed(2)}
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>

          {/* Right Column - Quick Scan History Widget */}
          <div className="lg:col-span-1 space-y-6">
            <WeeklyDigestWidget />
            <QuickScanHistory />
            <FrequentlyScanned />
          </div>
        </div>

        {/* Push notification KPIs — surfaces the order_push_log
            delivery health so pilot retailers can see their FCM stack
            in action. */}
        <PushOptInNudge />
        <NotificationFunnelWidget />
        <PushOpenRateWidget />

        {/* Scan Analytics Section - Collapsible */}
        <Card className="overflow-hidden">
          <CardHeader 
            className="cursor-pointer hover:bg-gray-50 transition-colors"
            onClick={() => setShowAnalytics(!showAnalytics)}
          >
            <div className="flex items-center justify-between">
              <CardTitle className="flex items-center gap-2">
                <BarChart3 className="w-5 h-5 text-blue-600" />
                {t('analytics.title', 'Scan Analytics')}
              </CardTitle>
              <Button variant="ghost" size="icon">
                {showAnalytics ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
              </Button>
            </div>
          </CardHeader>
          {showAnalytics && (
            <CardContent>
              <ScanAnalyticsDashboard />
            </CardContent>
          )}
        </Card>
      </div>
    </RetailerLayout>
  );
}
